import { useTranslation } from "react-i18next";

import { CLIENT_CODES, isProblem } from "@/shared/api/problem";

interface ProblemNoticeProps {
  error: unknown;
  /** API field names a form shows under its own inputs; the rest are listed here. */
  ownFields?: readonly string[];
  /**
   * What to say instead of the service's `title`, when the screen knows this
   * `code` and has better words for it. The code and the request id are shown
   * either way.
   */
  message?: string | undefined;
}

/**
 * A failure, said the same way everywhere: what happened, then the two things
 * support asks for, the `code` and the request id.
 */
export function ProblemNotice({ error, ownFields = [], message }: ProblemNoticeProps) {
  const { t } = useTranslation();
  const problem = isProblem(error) ? error : null;
  const fields = problem?.errors.filter(({ field }) => !ownFields.includes(field)) ?? [];

  return (
    <div
      role="alert"
      className="w-full rounded-control border border-danger-line bg-danger-tint px-2.5 py-2 text-sm text-fg"
    >
      <p className="font-medium text-danger">{message ?? words()}</p>
      {problem?.detail != null && <p className="mt-1">{problem.detail}</p>}
      {fields.length > 0 && (
        <ul className="mt-1 list-inside list-disc">
          {fields.map(({ field, code, message: text }) => (
            <li key={`${field}:${code}`}>
              <span className="num text-xs">{field}</span>: {text}
            </li>
          ))}
        </ul>
      )}
      {problem?.retryAfter != null && (
        <p className="mt-1">
          {t("errors.retry_after", { seconds: Math.ceil(problem.retryAfter) })}
        </p>
      )}
      {problem !== null && (
        <dl className="num mt-2 grid grid-cols-[auto_1fr] gap-x-2 text-xs text-fg-muted">
          <dt>{t("errors.code")}</dt>
          <dd data-testid="problem-code">{problem.code}</dd>
          {problem.requestId !== null && (
            <>
              <dt>{t("errors.request_id")}</dt>
              <dd data-testid="problem-request-id" className="break-all">
                {problem.requestId}
              </dd>
            </>
          )}
        </dl>
      )}
    </div>
  );

  function words(): string {
    if (problem === null) return t("errors.unknown");
    // Problems the app made up carry no text from a service.
    if (problem.code === CLIENT_CODES.network) return t("errors.network");
    if (problem.code === CLIENT_CODES.unexpected) return t("errors.unexpected");
    if (problem.code === "forbidden") return t("errors.forbidden");
    return problem.title;
  }
}
